import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, Pause, Play, Plus, Timer, X } from 'lucide-react';
import { recordDailyActivity, recordFocusSession } from '@cpu/progress-store';
import { useComfort } from './comfort';
import { playBell, playTick } from './sound';
import { celebrate } from './celebrate';
import { useToast } from './toast';

/**
 * The focus timer. A floating pill that expands into a calm, full-screen
 * block: one visible countdown ring, one goal line, nothing else. Based on
 * the Pomodoro rhythm (25/5 by default, adjustable). Completed blocks are
 * logged locally and feed the effort ledger and the activity heatmap.
 */

const PRESETS = [10, 15, 25, 50];

interface FocusContextValue {
  openLauncher: () => void;
  startSession: (minutes: number, label: string) => void;
  running: boolean;
  remainingSeconds: number;
  totalSeconds: number;
  label: string;
}

const FocusContext = createContext<FocusContextValue | null>(null);

type Phase = 'idle' | 'running' | 'paused' | 'done';

export function FocusProvider({ children }: { children: ReactNode }) {
  const { comfort, update } = useComfort();
  const { toast } = useToast();
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [label, setLabel] = useState('');
  const [goalDraft, setGoalDraft] = useState('');
  const deadlineRef = useRef(0);

  const startSession = useCallback((minutes: number, sessionLabel: string) => {
    setTotalSeconds(minutes * 60);
    setRemainingSeconds(minutes * 60);
    setLabel(sessionLabel.trim() || 'Deep work');
    deadlineRef.current = Date.now() + minutes * 60_000;
    setPhase('running');
    setLauncherOpen(false);
    playTick();
  }, []);

  const openLauncher = useCallback(() => setLauncherOpen(true), []);

  useEffect(() => {
    if (phase !== 'running') return;
    const tickTimer = window.setInterval(() => {
      const remaining = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        setPhase('done');
        playBell();
        celebrate();
        const minutes = Math.max(1, Math.round(totalSeconds / 60));
        void recordFocusSession(minutes, label);
        void recordDailyActivity(2);
        toast({ tone: 'success', title: `${minutes}-minute block complete`, body: 'Logged to today’s effort. Take a real break — five minutes away from the screen.' });
      }
    }, 250);
    return () => window.clearInterval(tickTimer);
  }, [phase, totalSeconds, label, toast]);

  const togglePause = useCallback(() => {
    setPhase((current) => {
      if (current === 'running') {
        setRemainingSeconds((value) => {
          deadlineRef.current = Date.now() + value * 1000;
          return value;
        });
        return 'paused';
      }
      if (current === 'paused') {
        deadlineRef.current = Date.now() + remainingSeconds * 1000;
        return 'running';
      }
      return current;
    });
    playTick();
  }, [remainingSeconds]);

  const stop = useCallback(() => {
    setPhase('idle');
    setRemainingSeconds(0);
  }, []);

  const value = useMemo<FocusContextValue>(() => ({
    openLauncher, startSession, running: phase === 'running', remainingSeconds, totalSeconds, label
  }), [openLauncher, startSession, phase, remainingSeconds, totalSeconds, label]);

  const progress = totalSeconds > 0 ? 1 - remainingSeconds / totalSeconds : 0;

  return (
    <FocusContext.Provider value={value}>
      {children}

      {phase !== 'idle' && (
        <>
          {phase !== 'done' && (
            <button
              className={`focus-pill ${phase === 'running' ? 'is-running' : ''}`}
              onClick={() => setLauncherOpen(true)}
              aria-label={`Focus block: ${label}, ${formatClock(remainingSeconds)} remaining. Open timer.`}
            >
              <span className="focus-pill-ring" style={{ '--p': progress } as React.CSSProperties} aria-hidden="true">
                <svg viewBox="0 0 36 36"><circle cx="18" cy="18" r="16" /><circle className="ring-fill" cx="18" cy="18" r="16" /></svg>
              </span>
              <span className="focus-pill-time">{formatClock(remainingSeconds)}</span>
              <span className="focus-pill-label">{label}</span>
              <Pause size={13} aria-hidden="true" />
            </button>
          )}

          {(launcherOpen || phase === 'done') && (
            <div className="focus-overlay" role="dialog" aria-modal="true" aria-label="Focus block">
              <div className="focus-sheet">
                {phase === 'done' ? (
                  <div className="focus-done">
                    <span className="focus-done-mark"><Check size={30} /></span>
                    <h2>{label} — done.</h2>
                    <p>{Math.round(totalSeconds / 60)} focused minutes are on today's ledger. Stand up, look far away, drink water. The next block is yours when you return.</p>
                    <div className="focus-sheet-actions">
                      <button className="btn-primary" onClick={() => { setPhase('idle'); playTick(); }}>Close</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="focus-sheet-head">
                      <span className="focus-kicker"><Timer size={15} />{phase === 'paused' ? 'Paused' : 'In session'}</span>
                      <button className="focus-close" onClick={() => setLauncherOpen(false)} aria-label="Hide timer (keeps running)"><X size={18} /></button>
                    </div>
                    <div className="focus-dial" style={{ '--p': progress } as React.CSSProperties}>
                      <svg viewBox="0 0 200 200" aria-hidden="true">
                        <circle cx="100" cy="100" r="90" />
                        <circle className="ring-fill" cx="100" cy="100" r="90" />
                      </svg>
                      <div className="focus-dial-readout">
                        <b>{formatClock(remainingSeconds)}</b>
                        <small>{phase === 'paused' ? 'paused' : `of ${Math.round(totalSeconds / 60)} minutes`}</small>
                      </div>
                    </div>
                    <p className="focus-goal">{label}</p>
                    <div className="focus-sheet-actions">
                      <button className="btn-ghost" onClick={togglePause}>
                        {phase === 'paused' ? <><Play size={16} />Resume</> : <><Pause size={16} />Pause</>}
                      </button>
                      <button className="btn-ghost" onClick={stop}><X size={16} />End block</button>
                    </div>
                    <small className="focus-note">The block keeps counting if you hide it. Time is real here.</small>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {launcherOpen && phase === 'idle' && (
        <div className="focus-overlay" role="dialog" aria-modal="true" aria-label="Start a focus block" onMouseDown={(event) => { if (event.target === event.currentTarget) setLauncherOpen(false); }}>
          <div className="focus-sheet">
            <div className="focus-sheet-head">
              <span className="focus-kicker"><Timer size={15} />New focus block</span>
              <button className="focus-close" onClick={() => setLauncherOpen(false)} aria-label="Close"><X size={18} /></button>
            </div>
            <h2>One block. One intention.</h2>
            <p>Write the single thing this block is for. When the ring runs out, you're free — guilt-free.</p>
            <input
              className="focus-goal-input"
              value={goalDraft}
              onChange={(event) => setGoalDraft(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') startSession(comfort.focusPresetMinutes, goalDraft); }}
              placeholder="e.g. trace one merge-sort pass by hand"
              maxLength={90}
              autoFocus
            />
            <div className="focus-presets" role="group" aria-label="Block length">
              {PRESETS.map((preset) => (
                <button
                  key={preset}
                  className={preset === comfort.focusPresetMinutes ? 'is-active' : ''}
                  onClick={() => update('focusPresetMinutes', preset)}
                >
                  {preset}<small>min</small>
                </button>
              ))}
              <button
                aria-label="Custom length"
                onClick={() => { const next = Number(window.prompt('Minutes for this block?', String(comfort.focusPresetMinutes))); if (Number.isFinite(next) && next >= 1 && next <= 180) update('focusPresetMinutes', Math.round(next)); }}
              ><Plus size={15} /></button>
            </div>
            <div className="focus-sheet-actions">
              <button className="btn-primary" onClick={() => startSession(comfort.focusPresetMinutes, goalDraft)}>
                <Play size={16} />Start {comfort.focusPresetMinutes} minutes
              </button>
            </div>
            <small className="focus-note">Finishing a block logs honest effort — nothing is awarded for merely opening the timer.</small>
          </div>
        </div>
      )}
    </FocusContext.Provider>
  );
}

export function useFocus(): FocusContextValue {
  const value = useContext(FocusContext);
  if (!value) throw new Error('useFocus must be used inside FocusProvider.');
  return value;
}

function formatClock(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
